import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  Pagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  SpeedDial,
  SpeedDialAction,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  AutoAwesome,
  Person,
  FilterList,
  Visibility,
  VisibilityOff,
  Add,
  Search,
  Delete,
  Upload,
  Download,
  Analytics,
  ExpandLess,
  ExpandMore,
  SmartToy,
  TrendingUp,
  Campaign,
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  YouTube,
  Psychology,
  Settings,
  CheckCircle,
  Error,
  Warning
} from '@mui/icons-material';

const UnifiedLeadIntelligence = () => {
  // Lead Management State
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filters, setFilters] = useState({ status: '', search: '', sortBy: 'created_at', sortOrder: 'DESC' });
  const [enrichmentStatus, setEnrichmentStatus] = useState({});
  const [enrichmentProgress, setEnrichmentProgress] = useState({});
  const [showEnrichmentDialog, setShowEnrichmentDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('basic');
  const [expandedLeads, setExpandedLeads] = useState(new Set());
  const [stats, setStats] = useState({ totalLeads: 0, enrichedLeads: 0, pendingEnrichment: 0, averageScore: 0, uniqueCompanies: 0 });

  // Social Media Lead Capture State
  const [activeTab, setActiveTab] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [setupDialog, setSetupDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [campaignConfig, setCampaignConfig] = useState({});
  const [campaignStats, setCampaignStats] = useState({
    totalCampaigns: 0,
    totalLeads: 0,
    activeCampaigns: 0
  });

  useEffect(() => {
    loadLeads();
    loadStats();
    loadCampaigns();
    loadPlatforms();
  }, [pagination.page, filters]);

  // Lead Management Functions
  const loadLeads = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/unified-leads?page=${pagination.page}&limit=${pagination.limit}&status=${filters.status}&search=${filters.search}&sortBy=${filters.sortBy}&sortOrder=${filters.sortOrder}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setLeads(data.leads);
        setPagination(prev => ({
          ...prev,
          total: data.total,
          pages: Math.ceil(data.total / pagination.limit)
        }));
      }
    } catch (error) {
      console.error('Failed to load leads:', error);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/unified-leads/stats/enrichment', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const enrichLead = async (leadId, method = 'advanced') => {
    try {
      setEnrichmentStatus(prev => ({ ...prev, [leadId]: 'running' }));
      setEnrichmentProgress(prev => ({ ...prev, [leadId]: 0 }));

      const response = await fetch(`/api/unified-leads/${leadId}/enrich`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ method })
      });

      const data = await response.json();
      if (data.success) {
        setEnrichmentStatus(prev => ({ ...prev, [leadId]: 'completed' }));
        setEnrichmentProgress(prev => ({ ...prev, [leadId]: 100 }));
        loadLeads(); // Refresh leads
        loadStats(); // Refresh stats
      }
    } catch (error) {
      console.error('Failed to enrich lead:', error);
      setEnrichmentStatus(prev => ({ ...prev, [leadId]: 'error' }));
    }
  };

  const toggleLeadExpansion = (leadId) => {
    setExpandedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  // Social Media Lead Capture Functions
  const loadCampaigns = async () => {
    try {
      const response = await fetch('/api/social-media-leads/campaigns/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns);
        setCampaignStats({
          totalCampaigns: data.totalCampaigns,
          totalLeads: data.totalLeads,
          activeCampaigns: data.campaigns.filter(c => c.leadCount > 0).length
        });
      }
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadPlatforms = async () => {
    try {
      const response = await fetch('/api/social-media-leads/platforms', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPlatforms(data.platforms);
      }
    } catch (error) {
      console.error('Failed to load platforms:', error);
    }
  };

  const handlePlatformSelect = async (platform) => {
    setSelectedPlatform(platform);
    try {
      const response = await fetch(`/api/social-media-leads/campaigns/template/${platform}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCampaignConfig(data.template);
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    }
    setSetupDialog(true);
  };

  const handleSetupCampaign = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/social-media-leads/campaigns/setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(campaignConfig)
      });
      const data = await response.json();
      if (data.success) {
        setSetupDialog(false);
        setCampaignConfig({});
        setSelectedPlatform('');
        loadCampaigns();
      } else {
        alert('Failed to setup campaign: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to setup campaign:', error);
      alert('Failed to setup campaign');
    }
    setLoading(false);
  };

  const handleRemoveCampaign = async (campaignKey) => {
    if (!window.confirm('Are you sure you want to remove this campaign?')) return;
    
    try {
      const response = await fetch(`/api/social-media-leads/campaigns/${campaignKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        loadCampaigns();
      }
    } catch (error) {
      console.error('Failed to remove campaign:', error);
    }
  };

  // Helper Functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'default';
      case 'Contacted': return 'primary';
      case 'Qualified': return 'success';
      case 'Hot': return 'error';
      case 'Converted': return 'success';
      default: return 'default';
    }
  };

  const getEnrichmentStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'running': return 'primary';
      case 'completed': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <Facebook />;
      case 'instagram': return <Instagram />;
      case 'linkedin': return <LinkedIn />;
      case 'twitter': return <Twitter />;
      case 'youtube': return <YouTube />;
      default: return <Campaign />;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'facebook': return '#1877F2';
      case 'instagram': return '#E4405F';
      case 'linkedin': return '#0A66C2';
      case 'twitter': return '#1DA1F2';
      case 'youtube': return '#FF0000';
      default: return '#666';
    }
  };

  // Render Functions
  const renderLeadCard = (lead) => (
    <Card key={lead.id} sx={{ mb: 2, p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="h6">{lead.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {lead.title && `${lead.title} at `}{lead.company}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip label={lead.status} color={getStatusColor(lead.status)} size="small" />
              <Chip label={`Score: ${lead.score}`} color="primary" size="small" />
              <Chip 
                label={lead.enrichment_status} 
                color={getEnrichmentStatusColor(lead.enrichment_status)} 
                size="small" 
              />
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Enrich with AI">
            <IconButton 
              size="small" 
              onClick={() => enrichLead(lead.id, 'chatgpt')}
              disabled={enrichmentStatus[lead.id] === 'running'}
            >
              <SmartToy />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => toggleLeadExpansion(lead.id)}>
              {expandedLeads.has(lead.id) ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {expandedLeads.has(lead.id) && (
        <Collapse in={expandedLeads.has(lead.id)}>
          <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="body2">
              <strong>Email:</strong> {lead.email}<br />
              <strong>Phone:</strong> {lead.phone}<br />
              <strong>Location:</strong> {lead.location}<br />
              <strong>Source:</strong> {lead.source}<br />
              <strong>Created:</strong> {new Date(lead.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Card>
  );

  const renderPlatformCard = (platform) => (
    <Card key={platform.key} sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ color: getPlatformColor(platform.key) }}>
            {getPlatformIcon(platform.key)}
          </Box>
          <Box>
            <Typography variant="h6">{platform.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Lead Ad Integration
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={() => handlePlatformSelect(platform.key)}
        >
          Setup Campaign
        </Button>
      </Box>
    </Card>
  );

  const renderCampaignRow = (campaign) => (
    <Card key={campaign.campaignKey} sx={{ mb: 2, p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ color: getPlatformColor(campaign.platform) }}>
            {getPlatformIcon(campaign.platform)}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
              {campaign.platform} Campaign
            </Typography>
            <Typography variant="body2" color="text.secondary" fontFamily="monospace">
              ID: {campaign.campaignId}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip
                label={`${campaign.leadCount} leads`}
                color="primary"
                size="small"
                icon={<Person />}
              />
              {campaign.autoEnrich && (
                <Chip label="Auto Enrich" size="small" color="success" />
              )}
              {campaign.createCustomAudience && (
                <Chip label="Custom Audience" size="small" color="info" />
              )}
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Remove Campaign">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemoveCampaign(campaign.campaignKey)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Lead Intelligence Hub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete lead lifecycle management - from capture to conversion
        </Typography>
      </Box>

      {/* Statistics Dashboard */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Person sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4">{stats.totalLeads}</Typography>
            <Typography variant="body2" color="text.secondary">
              Total Leads
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <AutoAwesome sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4">{stats.enrichedLeads}</Typography>
            <Typography variant="body2" color="text.secondary">
              Enriched Leads
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Campaign sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h4">{campaignStats.totalCampaigns}</Typography>
            <Typography variant="body2" color="text.secondary">
              Active Campaigns
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4">{stats.averageScore}</Typography>
            <Typography variant="body2" color="text.secondary">
              Avg Lead Score
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Lead Management" icon={<Person />} />
          <Tab label="Social Media Capture" icon={<Campaign />} />
          <Tab label="AI Enrichment" icon={<SmartToy />} />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Lead Management */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Lead Management</Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload />}
              >
                Import
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Collapse in={showFilters}>
            <Card sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Search"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    InputProps={{
                      startAdornment: <Search />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="New">New</MenuItem>
                      <MenuItem value="Contacted">Contacted</MenuItem>
                      <MenuItem value="Qualified">Qualified</MenuItem>
                      <MenuItem value="Hot">Hot</MenuItem>
                      <MenuItem value="Converted">Converted</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={filters.sortBy}
                      onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                      label="Sort By"
                    >
                      <MenuItem value="created_at">Created Date</MenuItem>
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="score">Score</MenuItem>
                      <MenuItem value="company">Company</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          </Collapse>

          {/* Leads List */}
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : leads.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Person sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Leads Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set up social media campaigns or import leads to get started
              </Typography>
            </Card>
          ) : (
            <Box>
              {leads.map(renderLeadCard)}
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pagination.pages}
                  page={pagination.page}
                  onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Social Media Lead Capture */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Social Media Lead Capture</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setActiveTab(1)}
            >
              Setup New Campaign
            </Button>
          </Box>

          {/* Platform Setup */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Setup Lead Capture Campaigns</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {platforms.map(renderPlatformCard)}
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Box mt={4}>
            <Typography variant="h6" gutterBottom>
              Active Campaigns
            </Typography>
            
            {campaigns.length === 0 ? (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Campaign sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Active Campaigns
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set up your first lead capture campaign to start capturing leads from your social media ads
                </Typography>
              </Card>
            ) : (
              <Box>
                {campaigns.map(renderCampaignRow)}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {/* AI Enrichment */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">AI-Powered Lead Enrichment</Typography>
            <Button
              variant="contained"
              startIcon={<SmartToy />}
              onClick={() => setShowEnrichmentDialog(true)}
            >
              Bulk Enrich
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>ChatGPT Enrichment:</strong> Real-time AI analysis of leads using ChatGPT<br />
              <strong>Advanced Enrichment:</strong> Comprehensive data gathering from multiple sources
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <SmartToy sx={{ color: 'primary.main' }} />
                  <Typography variant="h6">ChatGPT Enrichment</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Real-time AI analysis using ChatGPT to enrich lead data with insights, recommendations, and scoring.
                </Typography>
                <Button variant="outlined" startIcon={<AutoAwesome />}>
                  Learn More
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Psychology sx={{ color: 'success.main' }} />
                  <Typography variant="h6">Advanced Enrichment</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Comprehensive data gathering from multiple sources including company intelligence, social media, and behavioral analysis.
                </Typography>
                <Button variant="outlined" startIcon={<Analytics />}>
                  Learn More
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Campaign Setup Dialog */}
      <Dialog open={setupDialog} onClose={() => setSetupDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Setup Lead Capture Campaign - {selectedPlatform.toUpperCase()}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Campaign ID"
                value={campaignConfig.campaignId || ''}
                onChange={(e) => setCampaignConfig({...campaignConfig, campaignId: e.target.value})}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ad Account ID"
                value={campaignConfig.adAccountId || ''}
                onChange={(e) => setCampaignConfig({...campaignConfig, adAccountId: e.target.value})}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Access Token"
                value={campaignConfig.accessToken || ''}
                onChange={(e) => setCampaignConfig({...campaignConfig, accessToken: e.target.value})}
                margin="normal"
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Lead Form ID"
                value={campaignConfig.leadFormId || ''}
                onChange={(e) => setCampaignConfig({...campaignConfig, leadFormId: e.target.value})}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Campaign Settings
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={campaignConfig.autoEnrich || false}
                    onChange={(e) => setCampaignConfig({...campaignConfig, autoEnrich: e.target.checked})}
                  />
                }
                label="Auto-enrich leads with AI"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={campaignConfig.createCustomAudience || false}
                    onChange={(e) => setCampaignConfig({...campaignConfig, createCustomAudience: e.target.checked})}
                  />
                }
                label="Create custom audiences"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Enrichment Method</InputLabel>
                <Select
                  value={campaignConfig.enrichmentMethod || 'chatgpt'}
                  onChange={(e) => setCampaignConfig({...campaignConfig, enrichmentMethod: e.target.value})}
                  label="Enrichment Method"
                >
                  <MenuItem value="chatgpt">ChatGPT Enrichment</MenuItem>
                  <MenuItem value="advanced">Advanced Enrichment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSetupCampaign}
            variant="contained"
            disabled={loading || !campaignConfig.campaignId || !campaignConfig.accessToken}
          >
            {loading ? <CircularProgress size={20} /> : 'Setup Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Speed Dial for Quick Actions */}
      <SpeedDial
        ariaLabel="Quick Actions"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<Add />}
      >
        <SpeedDialAction
          icon={<Campaign />}
          tooltipTitle="Setup Campaign"
          onClick={() => setActiveTab(1)}
        />
        <SpeedDialAction
          icon={<SmartToy />}
          tooltipTitle="AI Enrichment"
          onClick={() => setActiveTab(2)}
        />
        <SpeedDialAction
          icon={<Upload />}
          tooltipTitle="Import Leads"
        />
      </SpeedDial>
    </Box>
  );
};

export default UnifiedLeadIntelligence;
